import React, { useEffect, useState } from 'react'
import { PageHeader } from '../../../../common/index'
import { Button } from '../../../../common'
import ResourceSection from '../../ResourceSection'
import ResourceItem from './ResourceItem'
import MarketingSection from './MarketingSection'
import { FiExternalLink } from 'react-icons/fi'
import { useNavigationContext } from '../../../../context/NavigationContext'
import { useDashboardAssociationContext } from '../../../../context/DashboardAssociationContext'
import { useQuery } from '@apollo/client'
import { getVisibleAssociationsWithTeamsQuery } from '../../../../queries/association'
import BookletsViewer from '../../team/material/BookletsViewer'
import QrDownloader from '../../team/material/QrDownloader'

const { REACT_APP_BRAND } = process.env;

const DashboardResources = () => {
  const { associationId, associationData, associationBooklets, shopShareDomain, defaultBooklet } = useDashboardAssociationContext()
  const { setNavigationActivated } = useNavigationContext()

  const [copiedItem, setCopiedItem] = useState<string | null>(null)

  // Check if this association is the only one on the domain
  const domain = shopShareDomain?.replace(/^https?:\/\//, '') || window.location.hostname;
  const { data: visibleAssociationsData, loading } = useQuery(getVisibleAssociationsWithTeamsQuery, {
    variables: { domain },
    skip: !associationData?.slug
  });

  const visibleAssociations = visibleAssociationsData?.visibleAssociationsWithTeams || [];
  const isOnlyAssociation = visibleAssociations.length === 1 &&
    visibleAssociations[0]?._id === associationId &&
    visibleAssociations[0]?.teams?.length === 0;

  // Use short URL if association is alone, otherwise use full URL
  const webshopUrl = isOnlyAssociation
    ? shopShareDomain || ''
    : `${shopShareDomain || ''}/${associationData?.slug || ''}`;

  const bookletUrl = `${shopShareDomain || ''}/vara-haften/${defaultBooklet?._id || ''}`;
  const resellerFormAssociationsUrl = associationData?.resellerFormAssociations?.startsWith('http')
    ? associationData.resellerFormAssociations
    : `https://${associationData?.resellerFormAssociations}`;
  const resellerFormTeamsUrl = associationData?.resellerFormTeams?.startsWith('http')
    ? associationData.resellerFormTeams
    : `https://${associationData?.resellerFormTeams}`;

  const isMainAssociation = defaultBooklet?.mainAssociationId === associationId;

  useEffect(() => {
    setNavigationActivated(true)
  }, [setNavigationActivated])


  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text)
    setCopiedItem(text)
  }

  const handleResourceBankClick = () => {
    window.open('https://cityhaftet.se/resursbank', '_blank')
  }

  const handleDownloadApp = () => {
    if (REACT_APP_BRAND === 'CITY') {
      window.open('https://cityhaftet.se/download', '_blank')
    } else {
      window.open('https://saenkelt.se/download', '_blank')
    }
  }

  const handleDownload = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div id="dashboard" className="resources">
      <PageHeader>
        <h1>Resurser</h1>
        <p>Länkar och material för marknadsföring</p>
      </PageHeader>
      <div className="container">
        {isMainAssociation && (
          <ResourceSection title="Länkar">
            <ResourceItem
              label="Webbshop"
              value={associationData && !loading ? webshopUrl : 'Laddar...'}
              onCopy={() => handleCopy(associationData && !loading ? webshopUrl : '')}
              isCopied={copiedItem === webshopUrl}
            />
            <ResourceItem
              label="Häftets innehåll"
              value={bookletUrl}
              onCopy={() => handleCopy(bookletUrl)}
              isCopied={copiedItem === bookletUrl}
            />
            {associationData?.resellerFormAssociations &&
              <ResourceItem
                label="Återförsäljarformulär, föreningar"
                value={resellerFormAssociationsUrl}
                onCopy={() => handleCopy(resellerFormAssociationsUrl)}
                isCopied={copiedItem === resellerFormAssociationsUrl}
              />
            }
            {associationData?.resellerFormTeams &&
              <ResourceItem
                label="Återförsäljarformulär, lag & klasser"
                value={resellerFormTeamsUrl}
                onCopy={() => handleCopy(resellerFormTeamsUrl)}
                isCopied={copiedItem === resellerFormTeamsUrl}
              />
            }
          </ResourceSection>
        )}

        {!isMainAssociation && (
          <ResourceSection title="Säljguide till medlemmar">
            <p>
              Praktiska tips och råd för att maximera försäljningen och nå era gemensamma säljmål.
            </p>
            <Button
              onClick={() => handleDownload('https://cityhaftet.se/resurser/Saljguide_vardebevis.pdf')}
              className="primary alt md"
            >
              <div className="flex gap-1 items-center justify-center">
                <span>Ladda ned säljguide</span>
              </div>
            </Button>
          </ResourceSection>
        )}

        <MarketingSection isMainAssociation={isMainAssociation} />

        {!isMainAssociation && (
          <>
            <QrDownloader
              name={associationData?.name || ''}
              qrCodeUrl={webshopUrl}
            />
            <ResourceSection title="Försäljningen i 6 steg">
              <p>
                Ert stöd hela vägen, från planering, genomförande, och betalning.
              </p>
              <Button
                onClick={() => handleDownload('https://cityhaftet.se/resurser/Forsaljning_6_steg.pdf')}
                className="primary alt md"
              >
                <div className="flex gap-1 items-center justify-center">
                  <span>Ladda ned instruktion</span>
                </div>
              </Button>
            </ResourceSection>

            <BookletsViewer
              associationBooklets={associationBooklets}
            />

            <ResourceSection title="Utdelningsunderlag">
              <p>
                Används för att dela ut värdebevis och samla in intäkter. Kan användas
                både när föreningen delar ut värdebevis till lag-/gruppledare och när
                dessa i sin tur delar ut till sina medlemmar.
              </p>
              <Button
                onClick={() => handleDownload('https://cityhaftet.se/wp-content/uploads/2025/09/Utdelningsunderlag.pdf')}
                className="primary alt md"
              >
                <div className="flex gap-1 items-center justify-center">
                  <span>Ladda ned utdelningsunderlag</span>
                </div>
              </Button>
            </ResourceSection>
            <ResourceSection title="Beställningslista">
              <p>
                Används för att samla in beställningar.
              </p>
              <Button
                onClick={() => handleDownload('https://cityhaftet.se/resurser/Bestallningslista.pdf')}
                className="primary alt md"
              >
                <div className="flex gap-1 items-center justify-center">
                  <span>Ladda ned beställningslista (pdf)</span>
                </div>
              </Button>
            </ResourceSection>
          </>
        )}

        {isMainAssociation && associationData?.resourceBank && (
          <ResourceSection
            title="Resursbanken"
            actionButton={{
              text: 'Resursbanken',
              onClick: handleResourceBankClick,
              variant: 'primary',
              icon: <FiExternalLink />
            }}
          >
            <ResourceItem
              label="Användarnamn"
              value={associationData?.resourceBank?.username || ''}
              onCopy={() => handleCopy(associationData?.resourceBank?.username || '')}
              isCopied={copiedItem === (associationData?.resourceBank?.username || '')}
            />
            <ResourceItem
              label="Lösenord"
              value={associationData?.resourceBank?.password || ''}
              onCopy={() => handleCopy(associationData?.resourceBank?.password || '')}
              isCopied={copiedItem === (associationData?.resourceBank?.password || '')}
            />
          </ResourceSection>
        )}
        {associationData?.demoBooklet && (
          <ResourceSection
            title="Demohäfte"
            actionButton={{
              text: 'Ladda ned appen',
              onClick: handleDownloadApp,
              variant: 'primary',
            }}
          >
            <ResourceItem
              label="E-post"
              value={associationData?.demoBooklet?.email || ''}
              onCopy={() => handleCopy(associationData?.demoBooklet?.email || '')}
              isCopied={copiedItem === (associationData?.demoBooklet?.email || '')}
            />
            <ResourceItem
              label="Lösenord"
              value={associationData?.demoBooklet?.password || ''}
              onCopy={() => handleCopy(associationData?.demoBooklet?.password || '')}
              isCopied={copiedItem === (associationData?.demoBooklet?.password || '')}
            />
          </ResourceSection>
        )}
      </div>
    </div>
  )
}

export default DashboardResources
